/**
 * Voicetudu Main App Component - New Chat-Based Flow
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from './components/Layout/Header';
import { Footer } from './components/Layout/Footer';
import { UploadZone } from './components/Upload/UploadZone';
import { AudioPreview } from './components/Upload/AudioPreview';
import { UploadProgress } from './components/Upload/UploadProgress';
import { LoadingSpinner } from './components/Common/LoadingSpinner';
import { ErrorMessage } from './components/Common/ErrorMessage';
import { TranscriptionViewer } from './components/Transcription/TranscriptionViewer';
import { ChatInterface } from './components/Chat/ChatInterface';
import { HistoryPanel } from './components/History/HistoryPanel';
import { useTranscription } from './hooks/useTranscription';
import { useChat } from './hooks/useChat';
import { saveHistoryItem } from './services/db.service';

/**
 * App states:
 * - idle: Initial state
 * - preview: File selected
 * - uploading: Uploading file
 * - transcribing: Extracting audio + transcribing
 * - transcription: Showing transcription text
 * - chatting: Chat interface open
 * - error: Error occurred
 */

function App() {
  const { t } = useTranslation('upload');
  const [appState, setAppState] = useState('idle');
  const [selectedFile, setSelectedFile] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [error, setError] = useState(null);

  // Hooks
  const { 
    transcription, 
    metadata, 
    loading: transcribing, 
    progress, 
    transcribe,
    reset: resetTranscription,
    setTranscriptionData
  } = useTranscription();

  const {
    messages,
    loading: chatLoading,
    startConversation,
    sendChatMessage,
    reset: resetChat
  } = useChat();

  // File selection
  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setAppState('preview');
    setError(null);
  };

  // Upload and transcribe
  const handleUpload = async () => {
    if (!selectedFile) return;

    setAppState('transcribing');
    setError(null);

    try {
      const result = await transcribe(selectedFile);
      
      // Save to history
      try {
        await saveHistoryItem({
          fileName: selectedFile.name,
          fileSize: selectedFile.size,
          fileType: selectedFile.type,
          result: result.data
        });
      } catch (err) {
        console.error('Failed to save history:', err);
      }

      // Initialize chat with transcription
      await startConversation(result.data.transcription, {
        filename: selectedFile.name,
        duration: result.data.duration
      });

      setAppState('transcription');
    } catch (err) {
      console.error('Transcription failed:', err);
      setError(err.message || 'Transcription failed');
      setAppState('error');
    }
  };

  // Start chatting
  const handleStartChat = async (quickType) => {
    setAppState('chatting');
    
    if (quickType) {
      // Send initial quick prompt
      const prompts = {
        executive: 'Genera un resumen ejecutivo de esta reunión',
        actions: 'Extrae todas las tareas accionables mencionadas',
        decisions: 'Lista las decisiones tomadas en esta reunión'
      };
      
      if (prompts[quickType]) {
        await sendChatMessage(prompts[quickType]);
      }
    }
  };

  // Back to transcription
  const handleBackToTranscription = () => {
    setAppState('transcription');
  };

  // Reset everything
  const handleReset = () => {
    setSelectedFile(null);
    setAppState('idle');
    setError(null);
    resetTranscription();
    resetChat();
  };

  // Remove file
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setAppState('idle');
    setError(null);
  };

  // Retry after error
  const handleRetry = () => {
    setError(null);
    setAppState('preview');
  };

  // Load from history
  const handleLoadHistory = async (item) => {
    setSelectedFile({
      name: item.fileName,
      size: item.fileSize,
      type: item.fileType
    });
    
    // Set transcription data from history
    setTranscriptionData(item.result.transcription, {
      duration: item.result.duration,
      processingTime: 0,
      isFromHistory: true
    });
    
    // Start conversation with history item
    await startConversation(item.result.transcription, {
      filename: item.fileName,
      duration: item.result.duration
    });
    
    setAppState('transcription');
  };

  // Render content based on state
  const renderContent = () => {
    switch (appState) {
      case 'idle':
        return <UploadZone onFileSelect={handleFileSelect} isLoading={false} />;

      case 'preview':
        return (
          <AudioPreview
            file={selectedFile}
            onUpload={handleUpload}
            onRemove={handleRemoveFile}
            isLoading={false}
          />
        );

      case 'transcribing':
        return (
          <div className="flex flex-col items-center justify-center py-12">
            <LoadingSpinner size="large" text="Extrayendo audio y transcribiendo..." />
            <div className="w-full max-w-xs mt-6">
              <div className="progress-bar">
                <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
              </div>
              <p className="mt-2 text-sm text-center text-gray-500">
                {progress < 50 ? 'Subiendo archivo...' : 'Transcribiendo con IA...'} ({progress}%)
              </p>
            </div>
            <p className="mt-4 text-xs text-gray-400 text-center max-w-sm">
              Esto puede tomar 20-60 segundos dependiendo del tamaño del archivo
            </p>
          </div>
        );

      case 'transcription':
        return (
          <TranscriptionViewer
            transcription={transcription}
            metadata={metadata}
            fileName={selectedFile?.name}
            onStartChat={handleStartChat}
            onReset={handleReset}
          />
        );

      case 'chatting':
        return (
          <ChatInterface
            messages={messages}
            loading={chatLoading}
            onSendMessage={sendChatMessage}
            onBack={handleBackToTranscription}
            fileName={selectedFile?.name}
          />
        );

      case 'error':
        return (
          <ErrorMessage
            error={{ message: error }}
            onRetry={handleRetry}
            onReset={handleReset}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 to-primary-50">
      <Header />
      
      {/* History button */}
      <div className="fixed top-20 right-4 z-40">
        <button
          onClick={() => setHistoryOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-white shadow-lg rounded-full text-gray-700 hover:text-primary-600 hover:shadow-xl transition-all"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
          <span className="hidden sm:inline">Historial</span>
        </button>
      </div>
      
      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card p-6 sm:p-8 min-h-[400px]">
          {renderContent()}
        </div>
      </main>

      <Footer />
      
      <HistoryPanel
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onLoadItem={handleLoadHistory}
      />
    </div>
  );
}

export default App;
